/**
 * EnvironmentalTable — data table for environmental reports with dynamic columns.
 *
 * 4 report types (temp, humidity, custom battery, full battery) x 2 view modes.
 * Full Battery only has summary.
 */

import { useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { convertIsoToLocal } from '../../../../libs.jsx';
import FilterPopover from '../../../community/residents/components/FilterPopover.jsx';
import ActiveFilterBar from '../../../community/residents/components/ActiveFilterBar.jsx';

// ---------------------------------------------------------------------------
// Column definitions per report type + view mode
// ---------------------------------------------------------------------------

export const COLUMN_DEFS_MAP = {
    1: { // Temperature
        detailed: [
            { key: 'local_create_time', label: 'Timestamp', sortable: true, filterable: false, required: true },
            { key: 'area_name', label: 'Area', sortable: true, filterable: true },
            { key: 'device_name', label: 'Device', sortable: true, filterable: true },
            { key: 'manufacture_uuid', label: 'Device Serial', sortable: true, filterable: false },
            { key: 'resident_name', label: 'Resident', sortable: true, filterable: true },
            { key: 'current_temperature', label: 'Current Temp', sortable: true, filterable: false },
        ],
        summary: [
            { key: 'device_name', label: 'Device', sortable: true, filterable: true, required: true },
            { key: 'manufacture_uuid', label: 'Device Serial', sortable: true, filterable: false },
            { key: 'area_name', label: 'Area', sortable: true, filterable: true },
            { key: 'resident_name', label: 'Resident', sortable: true, filterable: true },
            { key: 'avg_temperature', label: 'Avg Temp', sortable: true, filterable: false },
            { key: 'max_temperature', label: 'High Temp', sortable: true, filterable: false },
            { key: 'min_temperature', label: 'Low Temp', sortable: true, filterable: false },
        ],
    },
    2: { // Humidity
        detailed: [
            { key: 'local_create_time', label: 'Timestamp', sortable: true, filterable: false, required: true },
            { key: 'resident_name', label: 'Resident', sortable: true, filterable: true },
            { key: 'area_name', label: 'Area', sortable: true, filterable: true },
            { key: 'device_name', label: 'Device', sortable: true, filterable: true },
            { key: 'manufacture_uuid', label: 'Device Serial', sortable: true, filterable: false },
            { key: 'current_humidity', label: 'Current Humidity', sortable: true, filterable: false },
        ],
        summary: [
            { key: 'device_name', label: 'Device', sortable: true, filterable: true, required: true },
            { key: 'manufacture_uuid', label: 'Device Serial', sortable: true, filterable: false },
            { key: 'area_name', label: 'Area', sortable: true, filterable: true },
            { key: 'resident_name', label: 'Resident', sortable: true, filterable: true },
            { key: 'avg_humidity', label: 'Avg Humidity', sortable: true, filterable: false },
            { key: 'max_humidity', label: 'High Humidity', sortable: true, filterable: false },
            { key: 'min_humidity', label: 'Low Humidity', sortable: true, filterable: false },
        ],
    },
    3: { // Custom Low Battery
        detailed: [
            { key: 'local_create_time', label: 'Timestamp', sortable: true, filterable: false, required: true },
            { key: 'resident_name', label: 'Resident', sortable: true, filterable: true },
            { key: 'area_name', label: 'Area', sortable: true, filterable: true },
            { key: 'device_name', label: 'Device', sortable: true, filterable: true },
            { key: 'manufacture_uuid', label: 'Device Serial', sortable: true, filterable: false },
            { key: 'current_battery', label: 'Current Battery', sortable: true, filterable: false },
        ],
        summary: [
            { key: 'device_name', label: 'Device', sortable: true, filterable: true, required: true },
            { key: 'manufacture_uuid', label: 'Device Serial', sortable: true, filterable: false },
            { key: 'area_name', label: 'Area', sortable: true, filterable: true },
            { key: 'resident_name', label: 'Resident', sortable: true, filterable: true },
            { key: 'avg_battery', label: 'Avg Battery', sortable: true, filterable: false },
            { key: 'max_battery', label: 'High Battery', sortable: true, filterable: false },
            { key: 'min_battery', label: 'Low Battery', sortable: true, filterable: false },
        ],
    },
    4: { // Full Battery (summary only)
        detailed: [],
        summary: [
            { key: 'device_name', label: 'Device', sortable: true, filterable: true, required: true },
            { key: 'descriptions', label: 'Description', sortable: true, filterable: true },
            { key: 'battery_voltage', label: 'Current Battery', sortable: true, filterable: false },
            { key: 'last_modified', label: 'Last Recorded', sortable: true, filterable: false },
            { key: 'area_name', label: 'Area', sortable: true, filterable: true },
        ],
    },
};

function buildColumnLabels(columnDefs) {
    const labels = {};
    columnDefs.forEach((col) => { labels[col.key] = col.label; });
    return labels;
}

// ---------------------------------------------------------------------------
// Cell renderers
// ---------------------------------------------------------------------------

function formatDatetime(val) {
    if (!val) return '--';
    try {
        return convertIsoToLocal(val) || val;
    } catch { return val; }
}

function formatNumeric(val, unit) {
    if (val == null || val === '') return '--';
    const num = parseFloat(val);
    if (isNaN(num)) return '--';
    return (
        <span>{num.toLocaleString(undefined, { maximumFractionDigits: 2 })}{unit ? <span className="er-unit">{unit}</span> : null}</span>
    );
}

function renderCell(row, colKey, tzOffset) {
    const val = row[colKey];

    switch (colKey) {
        case 'resident_name':
            return row.resident_id ? (
                <Link to={`/profiles/resident/${row.resident_id}`} className="ar-link">{val || '--'}</Link>
            ) : (val || '--');

        case 'area_name':
            return row.area_id ? (
                <Link to={`/profiles/area/${row.area_id}`} className="ar-link">{val || '--'}</Link>
            ) : (val || '--');

        case 'device_name': {
            const mfgId = parseInt(row.manufacture_id, 10);
            const linkTo = mfgId === 1
                ? `/profiles/inovonics/${row.device_id}`
                : mfgId === 4
                    ? `/profiles/palcare/${row.device_id}`
                    : null;

            // Full battery shows stacked device + serial
            if (row.manufacture_uuid && !row._hideSerial) {
                return (
                    <div className="er-stacked-cell">
                        {linkTo && row.device_id ? (
                            <Link to={linkTo} className="ar-link">{val || '--'}</Link>
                        ) : (
                            <span>{val || '--'}</span>
                        )}
                        <span className="er-stacked-cell__sub">{row.manufacture_uuid}</span>
                    </div>
                );
            }

            return linkTo && row.device_id ? (
                <Link to={linkTo} className="ar-link">{val || '--'}</Link>
            ) : (val || '--');
        }

        case 'manufacture_uuid':
            return val || '--';

        case 'local_create_time':
            return formatDatetime(val);

        case 'last_modified':
            return val ? formatDatetime(val) : '--';

        case 'current_temperature':
        case 'avg_temperature':
        case 'max_temperature':
        case 'min_temperature':
            return formatNumeric(val, '°F');

        case 'current_humidity':
        case 'avg_humidity':
        case 'max_humidity':
        case 'min_humidity':
            return formatNumeric(val, '%');

        case 'current_battery':
        case 'avg_battery':
        case 'max_battery':
        case 'min_battery':
        case 'battery_voltage':
            return formatNumeric(val, 'V');

        case 'descriptions':
            return val || '--';

        default:
            return val != null ? String(val) : '--';
    }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const EnvironmentalTable = ({
    data, allData, columnDefs, visibleColumns,
    sortColumn, sortOrder, onSort,
    filters, onFilterChange, onClearFilters,
    showFilters = true, tzOffset = 0,
}) => {
    const columnLabels = useMemo(() => buildColumnLabels(columnDefs), [columnDefs]);

    const activeCols = useMemo(() => {
        return columnDefs.filter((col) => visibleColumns.includes(col.key));
    }, [columnDefs, visibleColumns]);

    const getFilterValues = useCallback((colKey) => {
        const vals = new Set();
        (allData || []).forEach((row) => {
            const v = row[colKey];
            if (v != null && v !== '') vals.add(String(v));
        });
        return [...vals].sort();
    }, [allData]);

    const handleRemoveFilter = useCallback((columnKey, value) => {
        const current = filters[columnKey] || [];
        onFilterChange(columnKey, current.filter((v) => v !== value));
    }, [filters, onFilterChange]);

    const hasActiveFilters = useMemo(() => {
        return Object.values(filters || {}).some((arr) => arr && arr.length > 0);
    }, [filters]);

    if (!data || data.length === 0) return null;

    return (
        <>
            {showFilters && hasActiveFilters ? (
                <ActiveFilterBar filters={filters} columnLabels={columnLabels} onRemoveFilter={handleRemoveFilter} onClearAll={onClearFilters} />
            ) : null}

            <div className="ap-table-container">
                <div className="dp-table-wrap">
                    <table className="dp-table">
                        <thead className="dp-thead">
                            <tr>
                                {activeCols.map((col) => (
                                    <th key={col.key} scope="col" className="ap-th">
                                        <div className="ap-th__content">
                                            {col.sortable ? (
                                                <button type="button" onClick={() => onSort(col.key)} className="ap-th__sort-btn" aria-label={`Sort by ${col.label}`}>
                                                    {col.label}
                                                    {sortColumn === col.key ? (
                                                        <svg className="ap-icon-sm" style={{ marginLeft: '2px', flexShrink: 0 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                                                            {sortOrder === 'asc'
                                                                ? <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
                                                                : <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />}
                                                        </svg>
                                                    ) : null}
                                                </button>
                                            ) : (<span>{col.label}</span>)}
                                            {showFilters && col.filterable ? (
                                                <FilterPopover columnKey={col.key} label={col.label} values={getFilterValues(col.key)} selectedValues={filters?.[col.key] || []} onChange={onFilterChange} />
                                            ) : null}
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {data.map((row, idx) => (
                                <tr key={row._rowId || idx} className="dp-tr">
                                    {activeCols.map((col) => (
                                        <td key={col.key} className="dp-td">{renderCell(row, col.key, tzOffset)}</td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </>
    );
};

export default EnvironmentalTable;
